import {PauseCircleOutline, PlayCircleOutline} from '@mui/icons-material';
import { Button } from "antd";
import React, { memo, useEffect,  useState } from "react";
import { useDispatch } from "react-redux";
import { setAudioChecker } from "store/audio/actions";
import { handleTextToSpeech } from "store/vocabulary/thunks";
import { femaleAI, maleAI } from "utils/assets/images";
import { useAudio } from "utils/context/AudioProvider";
import * as Sentry from "@sentry/react";

const AudioReader = ({ text, styles = {}, audioChecker, id, language }) => {
  const { playAudio, stopAudio, audioRef,  setCurrentAudio } =
    useAudio();
  const [buttonPress, setButtonPress] = useState(false);
  const [audio, setAudio] = useState(null);
  const [audioVersion, setAudioVersion] = useState("male");
  const [voiceSetting, setVoiceSetting] = useState({
    voice_languageCode: "",
    voice_name: "",
  });
  const dispatch = useDispatch();

  useEffect(() => {
    return () => {
      if (audioRef.current) audioRef.current.pause();
    };
  }, []);
  useEffect(() => {
    // Reset audio when voice setting changes
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      dispatch(setAudioChecker({ isPlaying: false, currentAudio: null }));
      //   setIsPlaying(false);
      stopAudio();
      setAudio(null);
    }
  }, [voiceSetting]);

  useEffect(() => {
    if (audioRef.current) {
      const handleAudioEnd = async () => {
        await dispatch(setAudioChecker({ isPlaying: false }));
        // setIsPlaying(false);
      };

      audioRef.current.addEventListener("ended", handleAudioEnd);

      return () => {
        audioRef.current.removeEventListener("ended", handleAudioEnd);
      };
    }
  }, [audioRef.current]);

  const handleVoiceChange = (event) => {
    // const value = event.target.value;
    const value = event;
    setAudioVersion(value);
    if (value === "female") {
      if (language === "ENGLISH") {
        setVoiceSetting({
          voice_languageCode: "en-US",
          voice_name: "en-US-Wavenet-F",
        });
      } else {
        setVoiceSetting({
          voice_languageCode: "es-ES",
          voice_name: "es-ES-Neural2-A",
        });
      }
    } else if (value === "male") {
      if (language === "ENGLISH") {
        setVoiceSetting({
          voice_languageCode: "en-US",
          voice_name: "en-US-Neural2-D",
        });
      } else {
        setVoiceSetting({
          voice_languageCode: "es-US",
          voice_name: "es-US-Standard-B",
        });
      }
    }
  };

  const stopCurrentAudio = async () => {
    await dispatch(setAudioChecker({ isPlaying: false, currentAudio: null }));
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setAudio(null);
    setCurrentAudio(null);
    stopAudio();
  };

  const createAudioFromText = async (voc, voiceSetting) => {
    const newIdea = {
      text: voc,
      pitch: "",
      volume: "",
      speed: audioVersion === "male" ? 0.9 : "",
      voice_name: voiceSetting.voice_name,
      voice_languageCodes: voiceSetting.voice_languageCode,
    };

    try {
      setButtonPress(true);
      const data = await handleTextToSpeech(newIdea);
      const audioBlob = new Blob([Uint8Array.from(data.body.data)], {
        type: "audio/mp3",
      });
      const url = URL.createObjectURL(audioBlob);

      setAudio(url);
      playAudio(url);
      await dispatch(
        setAudioChecker({ isPlaying: true, id, currentAudio: url })
      );
    } catch (error) {
      Sentry.captureException(`327 Error creating audio: ${error}`);
      console.error("Error creating audio:", error);
    } finally {
      setButtonPress(false);
    }
  };

  const toggleAudio = async (url) => {
    const isCurrentlyPlaying =
      audioChecker?.isPlaying && audioChecker?.id === id;

    if (isCurrentlyPlaying) {
      if (audioRef.current) {
        audioRef.current.pause();
        stopAudio();
      }
      await dispatch(setAudioChecker({ isPlaying: false }));
    } else {
      if (audioRef.current) {

       try {
        await audioRef.current.play();
        await dispatch(
          setAudioChecker({
            isPlaying: true,
            id,
            currentAudio: url || audioChecker?.currentAudio,
          })
        );
        } catch (error) {
          if (error.name !== 'AbortError') {
            Sentry.captureException(`Error playing audio: ${error}`);
            console.error('Error playing audio:', error);
          }
        }
      }
    }
  };

  const handleSpeaker = async (voc, voiceSetting) => {
    try {
      // Stop other playing audio if exists
      if (
        audioChecker?.currentAudio &&
        audioChecker?.isPlaying &&
        audioChecker?.id !== id
      ) {
        await stopCurrentAudio();
      }

      // If no audio exists for this item, create it
      if (!audioChecker?.currentAudio || audioChecker?.id !== id) {
        const url = await createAudioFromText(voc, voiceSetting);
        return;
      }

      // Toggle existing audio
      await toggleAudio();
    } catch (error) {
      Sentry.captureException(`328 Error in handleSpeaker: ${error}`);
      console.error("Error in handleSpeaker:", error);
      await stopCurrentAudio();
    }
  };
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        width: "100%",
        ...styles
      }}
    >
      <Button
        disabled={buttonPress || (audioChecker?.isPlaying && audioChecker.id === id)}
        type="link"
        style={{ borderRadius: 50 }}
        onClick={() => handleVoiceChange("female")}
        icon={
          <img
            src={femaleAI}
            style={{
              width: "100%",
              border: audioVersion === "female" ? "1px solid red" : 0,
              borderRadius: 50,
            }}
          />
        }
      />

      <Button
        disabled={buttonPress || (audioChecker?.isPlaying && audioChecker.id === id)}
        type="link"
        style={{ borderRadius: 50 }}
        onClick={() => handleVoiceChange("male")}
        icon={
          <img
            src={maleAI}
            style={{
              width: "100%",
              border: audioVersion === "male" ? "1px solid red" : 0,
              borderRadius: 50,
            }}
          />
        }
      />

      {/* <Button type="link" style={{ borderRadius: 50 }} disabled={buttonPress} onClick={() => handleSpeacker(text, voiceSetting)}
        icon={isPlaying ? <PauseCircleOutlineIcon /> : <PlayCircleOutlineIcon /> } loading={buttonPress} /> */}
      <Button
        type="link"
        style={{
          borderRadius: 50,
          position: "absolute",
          bottom: "-42px",
          right: "5px",
        }}
        className='playBtnCstm'
        disabled={buttonPress}
        onClick={() => handleSpeaker(text, voiceSetting)}
        icon={
          audioChecker?.id === id && audioChecker?.isPlaying ? (
            <PauseCircleOutline />
          ) : (
            <PlayCircleOutline />
          )
        }
        loading={buttonPress}
      />
    </div>
  );
};

export default memo(AudioReader);
